/**
 * Created by Ning Jiang on 2023-08-29.
 */

import {IReelStateEventData, ReelState} from "../../core/reel/ReelState";
import {APIEventSystem} from "@nolimitcity/slot-launcher/bin/interfaces/APIEventSystem";
import {DCRoadReelsView} from "./DCRoadReelsView";
import {SlotGame} from "@nolimitcity/slot-game/bin/SlotGame";
import {DevilsCrossRoad} from "../DevilsCrossRoad";
import {DCRoadSymbol} from "../symbol/DCRoadSymbol";
import {ScatterSymbol} from "../symbol/ScatterSymbol";
import {gsap, TimelineLite} from "gsap";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/utils/ArrayHelper";
import {WildSymbol} from "../symbol/WildSymbol";
import {DevilsCrossRoadSound} from "../sound/DevilsCrossRoadSound";

export class Quadrant {
    public readonly events:APIEventSystem;

    constructor() {
        this.events = SlotGame.apiPlugin.eventSystemFactory.create();
        this.events.on("ReelStateChange", (data:IReelStateEventData) => this.events.trigger(data.newState, data)); //To prevent event system from saving once callbacks if no on callbacks is set.
    }
}

export class DCRoadReelsController {

    public static DEV_WAYS_ORDER:number[] = [0, 1, 3, 2];
    public static DEV_WAYS_MAP:number[][] = [ // This is the server win case devWay map.
        [-1, 0, 0, 0, -1],
        [1, -1, 0, -1, 2],
        [1, 1, -1, 2, 2],
        [1, -1, 3, -1, 2],
        [-1, 3, 3, 3, -1]
    ];
    public static DEV_WAY_COORDS:[number, number][][] = [
        [[1, 2], [0, 3], [0, 2], [0, 1]],
        [[2, 1], [1, 0], [2, 0], [3, 0]],
        [[2, 3], [3, 4], [2, 4], [1, 4]],
        [[3, 2], [4, 1], [4, 2], [4, 3]]
    ];
    public static AVALANCHE_PATHS:[number, number][][] = [
        [[1, 2], [0, 2]],
        [[2, 1], [2, 0]],
        [[2, 3], [2, 4]],
        [[3, 2], [4, 2]]
    ];

    public reelView:DCRoadReelsView;
    public quadrants:Quadrant[] = [];

    private _reelStates:ReelState[] = [];
    public isQuickStop:boolean;
    private _isStopping:boolean;

    private _reelStopDelayTimer:TweenLite | null; //Delay for reel stop
    private _stopTl:TimelineLite | null;

    constructor() {
        this.quadrants = ArrayHelper.initArrayWithValues(4, index => new Quadrant());

        this.setIdle();
        this.reelView = new DCRoadReelsView();
        this.initSymbols();
    }

    private updateReelStates(value:ReelState):void {
        this.quadrants.forEach((quad, quadId) => {
            this.updateReelState(value, quadId);
        });
    }

    private updateReelState(value:ReelState, quadId:number) {
        if(value === this._reelStates[quadId]) {
            return;
        }

        const oldState = this._reelStates[quadId];
        this._reelStates[quadId] = value;
        this.quadrants[quadId].events.trigger("ReelStateChange", <IReelStateEventData>{
            quadId : quadId,
            newState : value,
            oldState : oldState
        });
    }

    private initSymbols():void {
        DevilsCrossRoad.model.data.reels.forEach((reel, reelId) => {
            reel.forEach((sym, symId) => {
                if(reelId === 2 && symId === 2) {
                    return;
                }
                if(sym === "Y" || sym === "X" || sym === "D") {
                    return;
                }
                this.addNewSymbol(sym, reelId, symId, true);
            });
        });

        this.reelView.initSpinIndicators();
    }

    private addNewSymbol(symName:string, reelId:number, symId:number, init:boolean = false):DCRoadSymbol {
        const oldSymbol = this.getSymbol(reelId, symId);
        if(oldSymbol) {
            if(oldSymbol.symbolName === symName) {
                return oldSymbol;
            } else {
                debugger;
            }
        }

        let symbol:DCRoadSymbol = DCRoadReelsController.createSymbol(symName, reelId, symId);
        this.reelView.addSymbol(symbol, init);

        return symbol;
    }

    public static createSymbol(symName:string, reelId:number, symId:number):DCRoadSymbol {
        if(symName.includes("S")) {
            return new ScatterSymbol(reelId, symId);
        }

        if(symName.includes("W")) {
            return new WildSymbol(reelId, symId);
        }

        return new DCRoadSymbol(symName, reelId, symId);
    }

    public getSymbol(reelId:number, symId:number):DCRoadSymbol | null {
        return this.reelView.getSymbol(reelId, symId);
    }

    public replaceSymbol(sym:DCRoadSymbol, newSymName:string):TimelineLite {
        const tl = gsap.timeline();
        let symbol:DCRoadSymbol = DCRoadReelsController.createSymbol(newSymName, sym.reelId, sym.symId);
        tl.add(this.reelView.addSymbol(symbol, false));
        tl.add(() => {
            this.reelView.removeSymbol(sym);
        });

        return tl;
    }

    public setIdle():void {
        this.updateReelStates(ReelState.IDLE);
        if(this._stopTl) {
            if(this._stopTl.isActive()) {
                this._stopTl.progress(1);
                this._stopTl.kill();
            }
            this._stopTl = null;
        }
    }

    public startSpin(isAvalanche:boolean):Promise<any> {
        this.isQuickStop = false;
        this._isStopping = false;

        this.updateReelStates(ReelState.START);
        const tl = gsap.timeline();
        tl.add([
            this.reelView.startSpin(isAvalanche),
            () => {
                DevilsCrossRoadSound.soundPlayer.playTrack({name : DevilsCrossRoadSound.REELS_OUT});
                DevilsCrossRoadSound.soundPlayer.playTrack({
                    name : DevilsCrossRoadSound.MAIN_GAME_SPINNING,
                    repeat : 10
                });
            }
        ]);
        tl.add(() => {
            this.updateReelStates(ReelState.SPIN);
        });

        return Promise.resolve(tl);
    }

    public tryStop(quickStop:boolean, delay:number):void {
        if(this.isQuickStop) {
            // Logger.logDev(`------------reel is already quick stopping.`);
            return;
        }
        this.isQuickStop = quickStop;

        if(this._isStopping) {
            // Logger.logDev(`------------reel is already stopping. start quick stop stopping`);
            if(this._stopTl && this._stopTl.isActive()) {
                this._stopTl.pause();
                this._stopTl.kill();
                this._stopTl = null;
            }
        }

        if(this._reelStopDelayTimer && this._reelStopDelayTimer.isActive()) {
            this._reelStopDelayTimer.pause();
            this._reelStopDelayTimer.kill();
        }

        if(delay > 0) {
            this._reelStopDelayTimer = gsap.to(this, {duration : delay, onComplete : () => this.tryStop(quickStop, 0)});
            return;
        }

        this._isStopping = true;
        this._stopTl = this.reelView.stopSpin(DevilsCrossRoad.model.data.reels, this.isQuickStop, (symName:string, reelId:number, symId:number, init:boolean) => this.addNewSymbol(symName, reelId, symId, init), (quadId:number) => this.updateReelState(ReelState.STOPPED, quadId));
        this._stopTl.add(() => {
            DevilsCrossRoadSound.soundPlayer.playTrack({name : DevilsCrossRoadSound.MAIN_GAME_SPINNING_STOP});
            DevilsCrossRoadSound.fadeAndStopSound({
                name : DevilsCrossRoadSound.MAIN_GAME_SPINNING,
                to : 0,
                duration : 50
            })
        });
    }

    public async avalanche():Promise<any> {
        this.updateReelStates(ReelState.AVALANCHE);
        await this.reelView.avalanche();
        this.setIdle();
        return Promise.resolve();
    }

    public startNearWin():void {
        this.reelView.startNearWin();
        DevilsCrossRoad.backgroundElements.setMainFrame("glow_intense", true);
    }

    public stopNearWin():void {
        this.reelView.stopNearWin();
        DevilsCrossRoad.backgroundElements.setMainFrame("glow_slow", true);
    }
}